// src/Pages/Home.js
import React, { useState, useEffect } from 'react';
import HeroHeader from '../components/HeroHeader';
import { Container, Box, Typography, CircularProgress, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { fetchCalendarEvents, fetchImagesFromDrive } from '../utils/googleApi';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TikTokSection from '../components/TikTokSection';
import ScrollReveal from '../components/ScrollReveal';

// Swiper imports
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation, Autoplay } from 'swiper';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

// Helper: format event date into a nice badge
const formatDateBadge = (event) => {
  const dateStr = event.start.dateTime || event.start.date;
  const date = new Date(dateStr);
  const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  const day = date.getDate();
  return { month, day };
};

// Helper: format time range
const formatTimeRange = (event) => {
  if (!event.start.dateTime) return 'All Day';
  const start = new Date(event.start.dateTime);
  const end = event.end.dateTime ? new Date(event.end.dateTime) : null;
  const startStr = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (!end) return startStr;
  const endStr = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${startStr} - ${endStr}`;
};

const Home = () => {
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [slides, setSlides] = useState([]);
  const [slidesLoading, setSlidesLoading] = useState(true);

  useEffect(() => {
    // Fetch calendar events
    const calendarId = process.env.REACT_APP_GOOGLE_CALENDAR_ID;
    fetchCalendarEvents(calendarId)
      .then(combinedEvents => setEvents(combinedEvents))
      .catch(error => console.error('Error fetching events:', error))
      .finally(() => setEventsLoading(false));

    // Fetch slideshow images from its own Drive folder
    const slideshowFolderId = process.env.REACT_APP_GOOGLE_DRIVE_SLIDESHOW_FOLDER_ID;
    if (slideshowFolderId) {
      fetchImagesFromDrive(slideshowFolderId)
        .then(images => {
          if (images && images.length > 0) {
            setSlides(images.map(img => img.srcLarge));
          }
        })
        .catch(error => console.error('Error fetching slideshow images:', error))
        .finally(() => setSlidesLoading(false));
    } else {
      setSlidesLoading(false);
    }
  }, []);

  return (
    <div>
      {/* ===== HERO ===== */}
      <HeroHeader />

      {/* ===== ABOUT SECTION ===== */}
      <Box sx={{ backgroundColor: '#000', py: { xs: 6, md: 8 } }}>
        <ScrollReveal>
        <Container maxWidth="md">
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              color: 'white',
              fontFamily: '"Audiowide", sans-serif',
              fontSize: { xs: '1.5rem', md: '2rem' },
              mb: 3,
            }}
          >
            Craft BBQ Done Right
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: 'rgba(255,255,255,0.8)',
              fontFamily: 'Roboto, sans-serif',
              fontSize: { xs: '16px', md: '18px' },
              lineHeight: 1.8,
              maxWidth: '650px',
              mx: 'auto',
            }}
          >
            At Left Field BBQ, we smoke our meats low and slow using traditional Texas methods
            and premium hardwoods. From whole briskets to our signature handhelds, every cut is
            prepared with care and served with pride. Whether you're stopping by for a meal or
            booking us for your next event, we bring the heat.
          </Typography>
        </Container>
        </ScrollReveal>
      </Box>

      {/* ===== UPCOMING EVENTS ===== */}
      <Box sx={{ backgroundColor: '#F6F4F1', py: { xs: 6, md: 8 } }}>
        <ScrollReveal>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              mb: 1,
              fontFamily: '"Audiowide", sans-serif',
              fontWeight: 'normal',
              color: 'black',
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            Upcoming Events
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              mb: 4,
              fontFamily: 'Roboto, sans-serif',
              color: 'rgba(0,0,0,0.5)',
              fontSize: '16px',
            }}
          >
            Find us at these upcoming locations
          </Typography>

          {eventsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: 'black' }} />
            </Box>
          ) : events.length === 0 ? (
            <Typography
              variant="body1"
              sx={{ textAlign: 'center', color: 'rgba(0,0,0,0.5)', fontFamily: 'Roboto, sans-serif' }}
            >
              No upcoming events — check back soon!
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: 3,
              }}
            >
              {events.map(event => {
                const { month, day } = formatDateBadge(event);
                const timeRange = formatTimeRange(event);

                return (
                  <Box
                    key={event.id}
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: 2,
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                      },
                      display: 'flex',
                      flexDirection: 'row',
                    }}
                  >
                    {/* Date badge */}
                    <Box
                      sx={{
                        backgroundColor: '#000',
                        color: 'white',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: { xs: 70, md: 80 },
                        px: 2,
                        py: 2,
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Roboto, sans-serif',
                          fontWeight: 'bold',
                          fontSize: '12px',
                          letterSpacing: '1px',
                          color: '#72faff',
                        }}
                      >
                        {month}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Roboto, sans-serif',
                          fontWeight: 'bold',
                          fontSize: { xs: '28px', md: '32px' },
                          lineHeight: 1,
                        }}
                      >
                        {day}
                      </Typography>
                    </Box>

                    {/* Event details */}
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 100 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: 'Roboto, sans-serif',
                          fontWeight: 'bold',
                          color: 'black',
                          fontSize: { xs: '15px', md: '16px' },
                          lineHeight: 1.3,
                          mb: 1,
                        }}
                      >
                        {event.summary || 'Event'}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 16, color: 'rgba(0,0,0,0.4)' }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'Roboto, sans-serif',
                            color: 'rgba(0,0,0,0.6)',
                            fontSize: '13px',
                          }}
                        >
                          {timeRange}
                        </Typography>
                      </Box>

                      {event.location && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                          <LocationOnIcon sx={{ fontSize: 16, color: 'rgba(0,0,0,0.4)', mt: '2px' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'Roboto, sans-serif',
                              color: 'rgba(0,0,0,0.6)',
                              fontSize: '13px',
                              lineHeight: 1.4,
                            }}
                          >
                            {event.location}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Container>
        </ScrollReveal>
      </Box>

      {/* ===== PHOTO GALLERY (only shows if slideshow folder is configured and has images) ===== */}
      {slidesLoading ? (
        <Box sx={{ backgroundColor: '#000', py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: 'white' }} />
        </Box>
      ) : slides.length > 0 ? (
        <Box sx={{ backgroundColor: '#000', py: { xs: 6, md: 8 } }}>
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              sx={{
                textAlign: 'center',
                mb: 1,
                fontFamily: '"Audiowide", sans-serif',
                fontWeight: 'normal',
                color: 'white',
                fontSize: { xs: '1.5rem', md: '2rem' },
              }}
            >
              From the Pit
            </Typography>
            <Typography
              sx={{
                textAlign: 'center',
                mb: 4,
                fontFamily: 'Roboto, sans-serif',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '16px',
              }}
            >
              A look at what's coming off the smoker
            </Typography>

            <Swiper
              modules={[Pagination, Navigation, Autoplay]}
              spaceBetween={16}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              autoplay={{ delay: 4000, disableOnInteraction: false }}
              loop
              breakpoints={{
                640: { slidesPerView: 2, spaceBetween: 16 },
                1024: { slidesPerView: 3, spaceBetween: 20 },
              }}
              style={{ borderRadius: 8, overflow: 'hidden', paddingBottom: '40px' }}
            >
              {slides.map((src, idx) => (
                <SwiperSlide key={idx}>
                  <Box
                    component="img"
                    src={src}
                    alt={`BBQ ${idx + 1}`}
                    sx={{
                      width: '100%',
                      height: { xs: 300, sm: 280, md: 320 },
                      objectFit: 'contain',
                      backgroundColor: '#111',
                      borderRadius: 2,
                      display: 'block',
                    }}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </Container>
        </Box>
      ) : null}

      {/* ===== TIKTOK SECTION (renders nothing if not configured) ===== */}
      <TikTokSection />

      {/* ===== CATERING CTA ===== */}
      <Box
        sx={{
          backgroundColor: '#F6F4F1',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <ScrollReveal>
        <Container maxWidth="sm">
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Audiowide", sans-serif',
              fontWeight: 'normal',
              color: 'black',
              fontSize: { xs: '1.5rem', md: '2rem' },
              mb: 2,
            }}
          >
            Let Us Cater Your Next Event
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              color: 'rgba(0,0,0,0.6)',
              fontSize: { xs: '16px', md: '18px' },
              lineHeight: 1.7,
              mb: 4,
            }}
          >
            From backyard cookouts to corporate events, we bring the full Left Field BBQ
            experience to you. Fill out a quick form and we'll get back to you with a custom quote.
          </Typography>
          <Button
            component={Link}
            to="/catering"
            variant="contained"
            sx={{
              backgroundColor: 'black',
              color: 'white',
              fontFamily: '"Audiowide", sans-serif',
              fontSize: { xs: '14px', md: '16px' },
              px: 5,
              py: 1.5,
              borderRadius: '4px',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#333' },
            }}
          >
            Request Catering
          </Button>
        </Container>
        </ScrollReveal>
      </Box>
    </div>
  );
};

export default Home;
